/**
 * What is about to stop somebody working.
 *
 * The one part of the fleet page with a deadline on it, which is why it sits above the
 * drivers and the cars rather than under them. Without it a firm learns that an insurance
 * certificate lapsed from a driver standing in a car park at six in the morning who cannot
 * sign on — the support call this page exists to prevent, and the reason the blueprint's §05
 * promised expiry reminders in the first place.
 *
 * Already-expired documents are on the list rather than filtered off it, at the top, with a
 * count of days that has gone negative. A list of things about to go wrong that quietly drops
 * the ones which already have is worse than no list at all — so the expired row is a red
 * pill rather than the greyed-out row it used to be, which said the opposite of what it meant.
 */

import { useTranslation } from 'react-i18next';

import { useTenantFormats } from '../../shell/formats';
import { EmptyState, Pill, Section, Sub, Table, Td, Th, Tr } from '../../ui';
import type { ExpiringDocument } from './api';

export function ExpiringList({ documents }: { documents: ExpiringDocument[] }) {
  const { t } = useTranslation();
  const formats = useTenantFormats();

  return (
    <Section title={t('fleet.expiring.title')} description={t('fleet.expiring.help')}>
      {documents.length === 0 ? (
        <EmptyState>{t('fleet.expiring.none')}</EmptyState>
      ) : (
        <Table>
          <thead>
            <Tr>
              <Th>{t('fleet.columns.who')}</Th>
              <Th>{t('fleet.documents.kind')}</Th>
              <Th>{t('fleet.columns.expires')}</Th>
            </Tr>
          </thead>
          <tbody>
            {documents.map((document) => (
              <Tr key={document.id}>
                <Td wrap>
                  {document.ownerName}
                  <Sub block>{t(`fleet.owner.${document.ownerType}`)}</Sub>
                </Td>
                <Td wrap>
                  {document.kind === 'Other' && document.description !== null
                    ? document.description
                    : t(`fleet.documentKinds.${document.kind}`)}
                </Td>
                <Td>
                  <Pill tone={document.isExpired ? 'danger' : 'warn'} soft>
                    {document.isExpired
                      ? t('fleet.expiring.expired', {
                          date: formats.day(document.expiresOn),
                          count: Math.abs(document.daysLeft),
                        })
                      : t('fleet.expiring.inDays', {
                          date: formats.day(document.expiresOn),
                          count: document.daysLeft,
                        })}
                  </Pill>
                </Td>
              </Tr>
            ))}
          </tbody>
        </Table>
      )}
    </Section>
  );
}
